import { FC, useCallback, useState } from 'react';

import { FileUploadDialogPresenter } from './FileUploadDialogPresenter';
import type { FileUploadDialogProps } from './types';

export const FileUploadDialog: FC<FileUploadDialogProps> = ({
  isOpen,
  onClose,
  onFileUpload,
  acceptedFileTypes = ['.csv', '.xlsx', '.xls'],
  maxFileSize = 10,
  maxFiles = 5,
  isLoading = false,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const handleFilesChange = useCallback(
    (files: File[]) => {
      setUploadedFiles((prev) => {
        const newFiles = [...prev, ...files];
        // Check total files limit
        if (newFiles.length > maxFiles) {
          // Keep only the first maxFiles files
          return newFiles.slice(0, maxFiles);
        }
        return newFiles;
      });
      setErrors([]);
    },
    [maxFiles],
  );

  const handleErrorsChange = useCallback((newErrors: string[]) => {
    setErrors(newErrors);
  }, []);

  const removeFile = useCallback((index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    setErrors([]);
  }, []);

  const handleUpload = useCallback(() => {
    if (uploadedFiles.length > 0 && onFileUpload && !isLoading) {
      onFileUpload(uploadedFiles);
      // Не очищаем файлы и не закрываем диалог здесь - это будет сделано в родительском компоненте
    }
  }, [uploadedFiles, onFileUpload, isLoading]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setUploadedFiles([]);
      setErrors([]);
      onClose();
    }
  }, [onClose, isLoading]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isLoading) {
        handleClose();
      }
    },
    [handleClose, isLoading],
  );

  return (
    <FileUploadDialogPresenter
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      uploadedFiles={uploadedFiles}
      errors={errors}
      acceptedFileTypes={acceptedFileTypes}
      maxFileSize={maxFileSize}
      maxFiles={maxFiles}
      onFilesChange={handleFilesChange}
      onErrorsChange={handleErrorsChange}
      onRemoveFile={removeFile}
      onUpload={handleUpload}
      onClose={handleClose}
      isLoading={isLoading}
    />
  );
};
